// Разбор записи, вписанной ссылкой («Видео ссылка» на вкладке «Данные»).
//
// Файл, загруженный дропзоной, бэкенд ставит на разбор сам
// (services/interviewAutoPipeline.js), а вписанная ссылка так и лежала в поле:
// очередь о ней не узнавала, пока человек не открывал «Обзор» и не жал
// «Оценить». Со стороны это выглядело как «прикрепил ссылку - и ничего»:
// ни загрузки, ни шагов оценки, ни слова о том, где их искать.
//
// Здесь решается две вещи: пора ли запускать разбор по вписанной ссылке и что
// человеку про него написать. Логика отделена от вёрстки - её же проверяют тесты.

// Ссылка на запись: непустая строка. Что именно лежит по адресу (Google Диск,
// S3, прямой файл), решает очередь - фронт её не фильтрует, иначе рабочие
// ссылки отсеивались бы «на всякий случай».
export function hasVideoLink(value) {
    return Boolean(String(value || '').trim());
}

// Пора ли отправлять запись в очередь. Запускаем один раз и только с чистого
// листа: разбор без статуса - это «не запускали». Ошибку сами не повторяем
// (очередь ретраебл-сбои дожимает без нас), чтобы не зациклить запуск, и ждём
// первого ответа api (loaded) - иначе на каждой перезагрузке карточки уезжал
// бы второй разбор тех же часов.
export function shouldStartFromLink({videoLink, status, loaded, sending, attempted} = {}) {
    if (!hasVideoLink(videoLink)) return false;
    if (!loaded || sending || attempted) return false;
    return !status;
}

// Что показать под полем со ссылкой. tone - как блок подсвечен:
// work (разбор идёт), done (готов), error (очередь споткнулась).
const VIEWS = {
    queued: {
        tone: 'work',
        title: 'Запись в очереди на разбор',
        hint: 'Ждём свободный слот. Страницу можно закрыть — очередь не прервётся.',
    },
    downloading: {
        tone: 'work',
        title: 'Скачиваем запись по ссылке',
        hint: 'Забираем видео с хранилища и достаём из него звук.',
    },
    analyzing: {
        tone: 'work',
        title: 'Разбираем диалог',
        hint: 'Распознаём речь, делим реплики по участникам и оцениваем ответы. Это занимает часы.',
    },
    done: {
        tone: 'done',
        title: 'Разбор записи готов',
        hint: 'Расшифровка, оценки ответов и итог интервью — во вкладке «Обзор».',
    },
};

const STARTING = {
    tone: 'work',
    title: 'Отправляем запись на разбор',
    hint: 'Заберём видео по ссылке и запустим оценку — следить за шагами можно здесь.',
};

// Ошибку показываем причиной, а не общим «что-то пошло не так»: чаще всего
// по ссылке нет доступа, и это человек чинит сам.
function failure(message) {
    return {
        tone: 'error',
        title: 'Не удалось забрать запись по ссылке',
        hint: message
            ? `${message} Проверьте, что запись открыта по ссылке, и запустите разбор заново во вкладке «Обзор».`
            : 'Проверьте, что запись открыта по ссылке, и запустите разбор заново во вкладке «Обзор».',
    };
}

export function linkPipelineView({videoLink, status, message, starting} = {}) {
    if (!hasVideoLink(videoLink)) return null;
    if (status === 'error') return failure(String(message || '').trim());
    if (VIEWS[status]) return VIEWS[status];
    return STARTING;
}

// Шаг, на котором стоит дорожка под текстом: у разбора по ссылке те же шаги,
// что на «Обзоре», - пока запуск не подтверждён статусом, горит первый.
export function linkPipelineStep(status) {
    return VIEWS[status] ? status : 'queued';
}
